import React, { useMemo } from 'react'
import { filter } from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import { CellProps } from 'react-table'

import { useHasRole } from 'auth/hooks'
import { SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import format from 'civic-champs-shared/utils/format'
import { useFeatureEnabled } from 'core/feature/hooks'
import { ContactCell, ContactHeader, ExpandableCell, NameCell, PersonLinkCell } from 'core/table/cells/modern'
import { CurrencyCell, DateCell, FixedPointCell, PhoneNumberCell } from 'core/table/cells'
import { createDateValueSort } from 'components/table/sort'
import { DYNAMIC_DATE, DYNAMIC_NUMBER, DYNAMIC_TEXT } from 'core/table/filters'
import { formatDateFilterOperandForDisplay } from 'core/table/dateFilterUtils'
import { createStringSort, createTagsSort } from 'core/table/utils'
import { FIRST_LAST_NAME, GROUPS, STATUS, TAGS } from 'champion/utils/filters'
import { useGroups } from 'group/hooks/useGroups'
import { useTagsCollection } from 'tags/hooks/useTagsCollection'
import Tags from 'components/tag/Tags'
import { Tag } from 'new-mentorship/types'
import useGroupMemberActionCell from './useGroupMemberActionCell'

export const DEFAULT_GROUP_MEMBER_HIDDEN_COLUMNS = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'homePhoneNumber',
  'donations',
  'latestActivity',
  'memberSince',
  'birthDate',
  'address',
  'status',
]

export const useGroupMemberColumnStyles = makeStyles(() => ({
  selectColumn: {
    '&>div': {
      width: '46px',
    },
  },
  nameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  firstNameColumn: {
    '&>div': {
      width: '90px',
    },
  },
  lastNameColumn: {
    '&>div': {
      width: '110px',
    },
  },
  contactColumn: {
    '&>div': {
      width: '100px',
    },
  },
  emailColumn: {
    '&>div': {
      width: '210px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  mobileColumn: {
    '&>div': {
      width: '150px',
    },
  },
  homeColumn: {
    '&>div': {
      width: '150px',
    },
  },
  groupsColumn: {
    '&>div': {
      width: '310px',
    },
  },
  hoursColumn: {
    '&>div': {
      width: '100px',
    },
  },
  donationsColumn: {
    '&>div': {
      width: '110px',
    },
  },
  activityColumn: {
    '&>div': {
      width: '110px',
    },
  },
  invitedOnColumn: {
    '&>div': {
      width: '120px',
    },
  },
  memberSinceColumn: {
    '&>div': {
      width: '120px',
    },
  },
  tagsColumn: {
    '&>div': {
      width: '310px',
    },
  },
  addressColumn: {
    '&>div': {
      width: '300px',
    },
  },
  statusColumn: {
    '&>div': {
      width: '110px',
    },
  },
  actionsColumn: {
    '&>div': {
      width: '110px',
    },
  },
}))

export interface GroupMemberRow {
  id: number
  person: {
    id: number
    givenName: string
    familyName: string
    email?: string
    phoneNumber?: string
    homePhoneNumber?: string
    birthDate?: string
    emailVerified?: boolean
    phoneNumberVerified?: boolean
    address?: unknown
    emergencyContact?: unknown
  }
  status: {
    displayName: string
    code: string
  }
  createdAt?: string
  startedAt?: string
  latestActivity?: string
  groups?: string
  tags?: Tag[]
  totalVolunteerHours?: number
  totalDonations?: number
}

const useGroupMemberColumns = (group: { id: number; name?: string }, refresh?: () => void) => {
  const classes = useGroupMemberColumnStyles()
  const isSuperAdmin = useHasRole(SUPER_ADMIN)
  const isDonationEnabled = useFeatureEnabled('Donations')
  const isChampionTagsEnabled = useFeatureEnabled('ChampionsTags')
  const { groups, loading: groupsLoading } = useGroups()
  const [{ tags, loading: tagsLoading, initiallyLoaded: tagsInitiallyLoaded }] = useTagsCollection()
  const ActionCell = useGroupMemberActionCell(group, refresh)

  const columns = useMemo(
    () =>
      filter(
        [
          {
            id: 'name',
            Header: 'Name',
            accessor: 'person',
            Cell: PersonLinkCell,
            filter: FIRST_LAST_NAME,
            className: classes.nameColumn,
          },
          {
            id: 'firstName',
            Header: 'First Name',
            accessor: 'person.givenName',
            Cell: (props: any) => NameCell(props, undefined, 'person'),
            filter: DYNAMIC_TEXT,
            className: classes.firstNameColumn,
            sortType: createStringSort('firstName'),
          },
          {
            id: 'lastName',
            Header: 'Last Name',
            accessor: 'person.familyName',
            Cell: (props: any) => NameCell(props, undefined, 'person'),
            filter: DYNAMIC_TEXT,
            className: classes.lastNameColumn,
            sortType: createStringSort('lastName'),
          },
          {
            id: 'contact',
            Header: <ContactHeader />,
            accessor: 'person',
            Cell: ContactCell,
            disableSortBy: true,
            disableFilters: true,
            className: classes.contactColumn,
          },
          {
            id: 'email',
            Header: 'Email',
            accessor: 'person.email',
            filter: DYNAMIC_TEXT,
            className: classes.emailColumn,
            sortType: createStringSort('email'),
          },
          {
            id: 'mobile',
            Header: 'Mobile Phone',
            accessor: 'person.phoneNumber',
            Cell: PhoneNumberCell,
            filter: DYNAMIC_TEXT,
            className: classes.mobileColumn,
            sortType: createStringSort('mobile'),
          },
          {
            id: 'homePhoneNumber',
            Header: 'Home Phone',
            accessor: 'person.homePhoneNumber',
            Cell: PhoneNumberCell,
            filter: DYNAMIC_TEXT,
            className: classes.homeColumn,
            sortType: createStringSort('homePhoneNumber'),
          },
          {
            id: 'groups',
            Header: 'Groups',
            accessor: 'groups',
            Cell: ExpandableCell,
            filter: GROUPS,
            className: classes.groupsColumn,
            sortType: createStringSort('groups'),
            getFilterAutocomplete: () => groups.map((g: { name: string }) => g.name),
          },
          {
            id: 'hours',
            Header: 'Hours',
            accessor: 'totalVolunteerHours',
            Cell: FixedPointCell,
            filter: DYNAMIC_NUMBER,
            className: classes.hoursColumn,
          },
          {
            id: 'donations',
            Header: 'Donations',
            accessor: 'totalDonations',
            Cell: CurrencyCell,
            hideColumn: !isDonationEnabled || !isSuperAdmin,
            filter: DYNAMIC_NUMBER,
            className: classes.donationsColumn,
          },
          {
            id: 'latestActivity',
            Header: 'Latest Activity',
            accessor: 'latestActivity',
            Cell: DateCell,
            filter: DYNAMIC_DATE,
            className: classes.activityColumn,
            sortType: createDateValueSort('latestActivity'),
            getOperandTextValue: (operand: unknown, operator: string) =>
              formatDateFilterOperandForDisplay(operator, operand),
          },
          {
            id: 'invitedOn',
            Header: 'Invited On',
            accessor: 'createdAt',
            Cell: DateCell,
            filter: DYNAMIC_DATE,
            className: classes.invitedOnColumn,
            sortType: createDateValueSort('createdAt'),
            getOperandTextValue: (operand: unknown, operator: string) =>
              formatDateFilterOperandForDisplay(operator, operand),
          },
          {
            id: 'memberSince',
            Header: 'Member Since',
            accessor: 'startedAt',
            Cell: DateCell,
            filter: DYNAMIC_DATE,
            className: classes.memberSinceColumn,
            sortType: createDateValueSort('startedAt'),
            getOperandTextValue: (operand: unknown, operator: string) =>
              formatDateFilterOperandForDisplay(operator, operand),
          },
          {
            id: 'birthDate',
            Header: 'Date of Birth',
            accessor: 'person.birthDate',
            Cell: DateCell,
            filter: DYNAMIC_DATE,
            className: classes.activityColumn,
            sortType: createDateValueSort('birthDate'),
            getOperandTextValue: (operand: unknown, operator: string) =>
              formatDateFilterOperandForDisplay(operator, operand),
          },
          {
            id: 'tags',
            Header: 'Tags',
            accessor: 'tags',
            filter: TAGS,
            hideColumn: !isChampionTagsEnabled,
            Cell: ({ cell: { value } }: CellProps<GroupMemberRow>) => (
              <Tags
                expandable
                tags={(value || []).map((tag: Tag) => ({ label: tag.name, id: tag.id, color: tag.color }))}
              />
            ),
            className: classes.tagsColumn,
            getFilterAutocomplete: () => tags.map((tag: Tag) => ({ label: tag.name, value: tag.id, color: tag.color })),
            sortType: createTagsSort('tags'),
            isTags: true,
          },
          {
            id: 'address',
            Header: 'Address',
            accessor: ({ person }: GroupMemberRow) => format.address(person.address),
            Cell: ExpandableCell,
            disableFilters: true,
            className: classes.addressColumn,
          },
          {
            id: 'status',
            Header: 'Status',
            accessor: 'status.displayName',
            filter: STATUS,
            className: classes.statusColumn,
            getFilterAutocomplete: () => [
              { label: 'Member', value: 'Member' },
              { label: 'Applicant', value: 'Applicant' },
            ],
          },
          {
            id: 'actions',
            Header: 'Actions',
            accessor: 'status.displayName',
            Cell: ActionCell,
            disableSortBy: true,
            disableFilters: true,
            hideInSelector: true,
            className: classes.actionsColumn,
          },
        ],
        column => !column.hideColumn,
      ),
    [ActionCell, classes, groups, isChampionTagsEnabled, isDonationEnabled, isSuperAdmin, tags],
  )

  return {
    columns,
    columnFiltersLoading: groupsLoading || tagsLoading || !tagsInitiallyLoaded,
  }
}

export default useGroupMemberColumns
